'use client'

import { Transition } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Checkbox } from '@src/components/Checkbox/Checkbox'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { Spinner } from '@src/components/Spinner/Spinner'
import { schema } from '@src/constants/SignUpSchema'
import { useCart } from '@src/hooks/useCart'
import { useConsents } from '@src/hooks/useConsents'
import { useErrors } from '@src/hooks/useErrors'
import { useLocalizedRouter } from '@src/hooks/useLocalizedRouter'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'
import { useTrackCreateAccount, useTrackSubmitEmailAddress } from '@src/utils/analytics.util'
import { filterBuilderAttributes } from '@src/utils/builder.util'
import { SignUpErrorsGroup } from '@src/utils/constants.util'
import { log } from '@src/utils/log.util'
import { useRecaptchaV3 } from '@src/utils/recaptcha.util'
import { cn } from '@src/utils/tailwind'

import { Button } from '../Button/Button'
import { TextField } from '../TextField/TextField'
import { PasswordField } from './PasswordField'
import { defaultProps } from './SignUp.defaults'

export interface SignUpProps {
  termsAndConditionsLink: string
  privacyPolicyLink: string
  buttonTestId: string
  emailFields: {
    freeUserRedirectURL: string
    buttonText: string
  }
  attributes: any
  passwordFields: {
    redirectURL: string
    buttonText: string
    ruleMinEightMaxTwentyChars: string
    ruleOneUpper: string
    ruleOneLower: string
    ruleOneNumber: string
    ruleNoBannedChars: string
  }
}

export const SignUp = ({
  termsAndConditionsLink,
  privacyPolicyLink,
  buttonTestId,
  emailFields,
  passwordFields,
  attributes,
}: SignUpProps = defaultProps) => {
  const trackCreateAccount = useTrackCreateAccount()
  const trackSubmitEmailAddress = useTrackSubmitEmailAddress()
  const router = useLocalizedRouter()
  const [isLoading, setLoading] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)
  const { buttonText: emailButtonText, freeUserRedirectURL } = emailFields || {}
  const {
    buttonText: passwordButtonText,
    ruleMinEightMaxTwentyChars,
    ruleOneLower,
    ruleOneNumber,
    ruleOneUpper,
    ruleNoBannedChars,
    redirectURL: passwordRedirectUrl,
  } = passwordFields || {}
  const { t: ts } = useLocale('sign-up-form')
  const { t, transform, locale } = useLocale('sign-up-form-errors')
  const { cart } = useCart()
  const errorsModal = useErrors()
  const { data: consentData, sendConsent } = useConsents({ fetchConsents: true })
  const { getRecaptchaToken } = useRecaptchaV3()

  const corporateConsent = consentData?.consents.find((consent) => consent.slug === 'corporate')

  const formMethods = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: cart?.customerEmail,
    },
    resolver: yupResolver(schema),
  })
  const {
    register,
    trigger,
    watch,
    clearErrors,
    setValue,
    formState: { errors },
  } = formMethods

  const password = watch('password')
  const email = watch('email')

  const [isPasswordVisible, setPasswordVisible] = useState(false)

  useEffect(() => {
    if (corporateConsent) {
      setConsentChecked(corporateConsent.defaultCheckboxValue)
    }
  }, [corporateConsent])

  useEffect(() => {
    if (cart?.customerEmail) {
      setValue('email', cart.customerEmail)
    }
  }, [cart, setValue])

  useEffect(() => {
    if (email) {
      setPasswordVisible(false)
      setValue('password', '')
      clearErrors('password')
    }
  }, [clearErrors, email, setValue])

  const onEmailSubmit = async () => {
    if (!email || errors?.email?.message) return

    try {
      setLoading(true)
      const resp = await ServerActions.serial.setCustomerEmail(email)

      const isExistingFreeUser = resp?.customerFlow === 'ExistingUser'
      const isNewUser = resp?.customerFlow === 'NewUser'

      await ServerActions.serial.setCustomFieldToCart({
        name: 'customerFlow',
        value: String(resp?.customerFlow),
      })

      if (isExistingFreeUser) {
        await trackSubmitEmailAddress({ 'submit email result': 'existing', email })
        router.push(freeUserRedirectURL)
        return
      } else if (isNewUser) {
        await trackSubmitEmailAddress({ 'submit email result': 'new', email })
        setPasswordVisible(true)
      }

      setLoading(false)
    } catch (error: any) {
      log.error(`[SignUp] Error when setting customer email - ${error?.message}`, error)
      setLoading(false)
      errorsModal.setError(error, { group: SignUpErrorsGroup })
    }
  }

  const onPasswordSubmit = async () => {
    if (!email || !password || errors?.password?.message) return

    try {
      setLoading(true)
      trackCreateAccount()
      if (!corporateConsent?.consentText || !corporateConsent.showCheckbox) {
        void sendConsent({ email, slug: 'corporate', value: true })
      } else {
        void sendConsent({
          email,
          slug: 'corporate',
          value: consentChecked === corporateConsent?.checkIndicatesConsent,
        })
      }
      await getRecaptchaToken('createUser')
      await ServerActions.serial.createUser({
        email,
        password,
        locale: locale.codeSnakeCase,
      })
      router.push(passwordRedirectUrl)
    } catch (error: any) {
      log.error(`[SignUp] Error when creating user - ${error?.message}`, error)
      setLoading(false)
      errorsModal.setError(error, { group: SignUpErrorsGroup })
    }
  }

  const onClick = async () => {
    if (isLoading) return
    if (isPasswordVisible) {
      await trigger('password')
      await onPasswordSubmit()
    } else {
      await trigger('email')
      await onEmailSubmit()
    }
  }
  return (
    <div {...filterBuilderAttributes(attributes)}>
      <FormProvider {...formMethods}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
          }}
          className="mx-auto mt-7 flex w-full max-w-sm flex-col items-center"
        >
          <TextField
            labelText={ts('email')}
            id="email"
            data-testid="email"
            {...register('email', { shouldUnregister: false })}
            isInvalid={!!errors.email?.message}
            captionText={t(errors.email?.message)}
            disabled={isLoading}
            containerClassName={cn({ 'opacity-20': isLoading })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void onClick()
            }}
          />

          <Transition
            as="div"
            show={isPasswordVisible}
            enter="transform transition duration-350"
            enterFrom="opacity-0 -translate-y-[72px]"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-350"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="w-full"
          >
            <PasswordField
              maxLength={20}
              disabled={isLoading}
              ruleText={{
                ruleMinEightMaxTwentyChars,
                ruleOneLower,
                ruleOneNumber,
                ruleOneUpper,
                ruleNoBannedChars,
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void onClick()
              }}
            />
            {corporateConsent?.consentText && (
              <div className="mt-4 w-full">
                <div className="w-full border-t border-bodi-gray-light" />
                <div className="py-4">
                  {corporateConsent.showCheckbox ? (
                    <Checkbox
                      checked={consentChecked}
                      onChange={(event) => {
                        setConsentChecked(event.target.checked)
                      }}
                    >
                      <span
                        className="prose"
                        dangerouslySetInnerHTML={{ __html: corporateConsent?.consentText }}
                      />
                    </Checkbox>
                  ) : (
                    <span
                      className="prose text-body-sm"
                      dangerouslySetInnerHTML={{ __html: corporateConsent?.consentText }}
                    />
                  )}
                </div>
                <div className="w-full border-t border-bodi-gray-light" />
              </div>
            )}
          </Transition>

          <Button
            onClick={onClick}
            size="medium"
            className="mt-4 w-full"
            type="button"
            data-testid={buttonTestId}
            disabled={isLoading}
            isLoading={isLoading}
            loadingComponent={<Spinner size="medium" className="fill-bodi-black text-bodi-white" />}
          >
            {isPasswordVisible ? passwordButtonText : emailButtonText}
          </Button>

          <div className="w-5/6 py-6 text-center">
            <p className="mx-0 px-0 text-body-sm tracking-[.029px]">
              {transform(t('disclaimer'), {
                link1: (text) => (
                  <LocalizedLink
                    href={termsAndConditionsLink}
                    className="text-bodi-blue-info"
                    target="_blank"
                  >
                    {text}
                  </LocalizedLink>
                ),
                link2: (text) => (
                  <LocalizedLink
                    href={privacyPolicyLink}
                    className="text-bodi-blue-info"
                    target="_blank"
                  >
                    {text}
                  </LocalizedLink>
                ),
              })}
            </p>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
